import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import "./PercorsiNonMappati.css";
import RotateDevice from "../components/RotateDevice/RotateDevice";
import Lf23Loader from "../components/Lf23Loader";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

const legendMap = {
    0: { label: "OK", desc: "Percorsi registrati correttamente" },
    1: { label: "No Percorsi (1 sett.)", desc: "Mezzo non registra percorsi da almeno 1 settimana" },
    2: { label: "Traccia Diversa", desc: "Percorsi presenti nella giornata, ma non per la traccia in oggetto" },
    3: { label: "Non Mossi", desc: "Veicolo non ha effettuato spostamenti" },
    4: { label: "Non Censiti", desc: "Veicolo non presente nel sistema" }
};

const getCellClass = (cell) => {
    if (!cell) return "grigio";
    const { stato, turno } = cell;

    if (stato === 3) {
        return turno === 1 ? "ocra" : "grigio";
    }

    const map = {
        0: "verde",
        1: "rosso",
        2: "rosso-tenue",
        3: "grigio",
        4: "celeste",
    };
    return map[stato] || "grigio";
};

const scenarioOptions = [
    { value: "ok", label: "OK"},
    { value: "non_censiti", label: "Non censiti"},
    { value: "no_percorsi", label: "Mezzo non registra percorsi da almeno 1 settimana"},
    { value: "altro_percorso", label: "Percorsi presenti nella giornata, ma non per la traccia in oggetto"},
    { value: "non_mossi", label: "Non mossi"},
];

const PercorsiNonMappati = () => {
    const navigate = useNavigate();
    const [dati, setDati] = useState([]);
    const [targa, setTarga] = useState("");
    const [selectedScenarios, setSelectedScenarios] = useState(
        scenarioOptions.filter(o => o.value !== "ok")
    );
    const [loading, setLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && isFullscreen) setIsFullscreen(false);
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isFullscreen]);
    const [searchTerm, setSearchTerm] = useState("");

    // Stats globali
    const [stats, setStats] = useState({
        veicoliAttivi: 0,
        veicoliProblematici: 0,
        mediaConformita: 0,
        nonCensiti: 0
    });

    const giorni = [...Array(7)].map((_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - (6 - i) - 1);
        return {
            date: d.toISOString().split("T")[0],
            dayName: d.toLocaleDateString('it-IT', { weekday: 'short' }).toUpperCase(),
            dayNumber: d.getDate(),
            monthName: d.toLocaleDateString('it-IT', { month: 'short' })
        };
    });

    const calculateStats = (data) => {
        if (!data || data.length === 0) {
            setStats({
                veicoliAttivi: 0,
                veicoliProblematici: 0,
                mediaConformita: 0,
                nonCensiti: 0
            });
            return;
        }

        let veicoliAttivi = 0;
        let veicoliProblematici = 0;
        let nonCensiti = 0;
        let totalGiorniOK = 0;
        let totalGiorni = 0;

        data.forEach(row => {
            const stati = Object.values(row).filter(v => typeof v === "object").map(v => v.stato);

            // Veicoli Attivi: hanno almeno un percorso registrato (stato 0)
            const hasOK = stati.includes(0);
            if (hasOK) veicoliAttivi++;

            // Veicoli Problematici: hanno più di 3 anomalie nella settimana (stati 1 o 2)
            const anomalie = stati.filter(s => s === 1 || s === 2).length;
            if (anomalie > 3) veicoliProblematici++;

            // Non Censiti: tutti i giorni hanno stato 4
            const allNonCensiti = stati.every(s => s === 4);
            if (allNonCensiti) nonCensiti++;

            // Calcolo conformità (% di giorni OK)
            const giorniOK = stati.filter(s => s === 0).length;
            totalGiorniOK += giorniOK;
            totalGiorni += stati.length;
        });

        const mediaConformita = totalGiorni > 0 ? Math.round((totalGiorniOK / totalGiorni) * 100) : 0;

        setStats({
            veicoliAttivi,
            veicoliProblematici,
            mediaConformita,
            nonCensiti
        });
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/webfleet/percorsi-non-mappati`, {
                params: { targa },
            });

            const active = selectedScenarios.map(s => s.value);
            const filtered = res.data.filter(row => {
                const stati = Object.values(row).filter(v => typeof v === "object").map(v => v.stato);

                const has0 = stati.includes(0);
                const has1 = stati.includes(1);
                const has2 = stati.includes(2);
                const only3 = stati.every(s => s === 3);
                const only4 = stati.every(s => s === 4);
                const allOkOr3 = stati.every(s => s === 0 || s === 3);

                const match = [];

                if (has0 && allOkOr3) match.push("ok");
                if (only4) match.push("non_censiti");
                if (has1) match.push("no_percorsi");
                if (has2) match.push("altro_percorso");
                if (only3) match.push("non_mossi");

                return match.some(m => active.includes(m));
            });

            setDati(filtered);

            // Calcola stats su TUTTI i dati (non filtrati per scenario)
            calculateStats(res.data);
        } catch (err) {
            console.error("Errore nel fetch dei percorsi non mappati:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, [selectedScenarios]);

    const handleSubmit = (e) => {
        e.preventDefault();
        fetchData();
    };

    const handleReset = () => {
        setTarga("");
        setSearchTerm("");
        setSelectedScenarios(scenarioOptions.filter(o => o.value !== "ok"));
        fetchData();
    };

    const filteredDati = dati.filter(row => {
        if (!searchTerm) return true;
        return row.targa?.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const getStatusIcon = (stato) => {
        const icons = {
            0: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
            ),
            1: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="15" y1="9" x2="9" y2="15"></line>
                    <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
            ),
            2: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
            ),
            3: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="8" y1="12" x2="16" y2="12"></line>
                </svg>
            ),
            4: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
            ),
        };
        return icons[stato] || null;
    };

    return (
        <div className="percorsi-mappati-container">
            <header className="page-header">
                <div className="header-content">
                    <button className="back-button" onClick={() => navigate('/produttivita')}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7"/>
                        </svg>
                        Indietro
                    </button>
                    <div className="header-title">
                        <div className="header-title-row">
                            <h2>Percorsi Non Mappati</h2>
                            <button
                                className="info-button-modern"
                                onClick={() => setShowInfo(!showInfo)}
                                title="Informazioni"
                            >
                                <span className="info-icon-text">i</span>
                            </button>
                        </div>
                        <p className="header-subtitle">Monitoraggio tracciamenti veicoli ultimi 7 giorni</p>
                    </div>
                </div>
            </header>

            {/* Modal Informazioni */}
            {showInfo && (
                <div className="modal-overlay" onClick={() => setShowInfo(false)}>
                    <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-icon">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                </svg>
                            </div>
                            <h3>Informazioni sulla pagina</h3>
                            <button className="modal-close" onClick={() => setShowInfo(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="modal-section">
                                <h4>Cosa fa questa pagina?</h4>
                                <p>
                                    In questa sezione vengono visualizzate le <strong>targhe monitorate negli ultimi 7 giorni</strong>,
                                    indicando per ciascun giorno se sono stati rilevati <strong>percorsi coerenti</strong> oppure{" "}
                                    <strong>anomalie</strong> relative alla mancata registrazione dei tragitti.
                                </p>
                                <p>
                                    Il colore associato a ciascun giorno rappresenta il tipo di anomalia riscontrata,
                                    permettendo un'analisi immediata e dettagliata dello stato operativo dei mezzi.
                                </p>
                            </div>
                            <div className="modal-section">
                                <h4>Statistiche KPI</h4>
                                <p>
                                    Le statistiche mostrate nelle card sono calcolate globalmente su tutti i veicoli monitorati:
                                </p>
                                <ul style={{ marginLeft: '20px', marginTop: '10px', lineHeight: '1.8' }}>
                                    <li>
                                        <strong>Veicoli Attivi:</strong> Numero di veicoli che hanno registrato almeno un percorso corretto
                                        (stato 0) durante i 7 giorni monitorati.
                                    </li>
                                    <li>
                                        <strong>Veicoli Problematici:</strong> Numero di veicoli con più di 3 anomalie gravi
                                        (stati 1 o 2) nell'arco della settimana.
                                    </li>
                                    <li>
                                        <strong>Media Conformità:</strong> Percentuale media di giorni con percorsi conformi.
                                        Calcolata come: <em>(totale giorni OK di tutti i veicoli / totale giorni monitorati) × 100</em>.
                                        Rappresenta il livello di conformità complessivo della flotta.
                                    </li>
                                    <li>
                                        <strong>Non Censiti:</strong> Numero di veicoli che risultano completamente assenti dal sistema
                                        (stato 4 per tutti i 7 giorni).
                                    </li>
                                </ul>
                            </div>

                            <div className="modal-section">
                                <h4>Legenda Stati</h4>
                                <div className="legend-grid">
                                    <div className="legend-item">
                                        <span className="legend-badge verde">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                        </span>
                                        <div className="legend-text">
                                            <strong>0 - OK</strong>
                                            <span>{legendMap[0].desc}</span>
                                        </div>
                                    </div>
                                    <div className="legend-item">
                                        <span className="legend-badge rosso">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                                <line x1="6" y1="6" x2="18" y2="18"></line>
                                            </svg>
                                        </span>
                                        <div className="legend-text">
                                            <strong>1 - {legendMap[1].label}</strong>
                                            <span>{legendMap[1].desc}</span>
                                        </div>
                                    </div>
                                    <div className="legend-item">
                                        <span className="legend-badge rosso-tenue">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                            </svg>
                                        </span>
                                        <div className="legend-text">
                                            <strong>2 - {legendMap[2].label}</strong>
                                            <span>{legendMap[2].desc}</span>
                                        </div>
                                    </div>
                                    <div className="legend-item">
                                        <span className="legend-badge grigio">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <line x1="8" y1="12" x2="16" y2="12"></line>
                                            </svg>
                                        </span>
                                        <div className="legend-text">
                                            <strong>3 - Non Mossi (senza turno)</strong>
                                            <span>Veicolo fermo senza turno assegnato</span>
                                        </div>
                                    </div>
                                    <div className="legend-item">
                                        <span className="legend-badge ocra">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <line x1="8" y1="12" x2="16" y2="12"></line>
                                            </svg>
                                        </span>
                                        <div className="legend-text">
                                            <strong>3 - Non Mossi (con turno)</strong>
                                            <span>Veicolo fermo con turno assegnato</span>
                                        </div>
                                    </div>
                                    <div className="legend-item">
                                        <span className="legend-badge celeste">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                            </svg>
                                        </span>
                                        <div className="legend-text">
                                            <strong>4 - {legendMap[4].label}</strong>
                                            <span>{legendMap[4].desc}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="content-area">
                {/* Top Controls */}
                <div className="top-controls">
                    <div className="controls-left">
                        <button
                            className={`toggle-filters-btn ${showFilters ? 'active' : ''}`}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                            </svg>
                            {showFilters ? 'Nascondi Filtri' : 'Mostra Filtri'}
                        </button>

                        <div className="search-bar-compact">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="m21 21-4.35-4.35"></path>
                            </svg>
                            <input
                                type="text"
                                placeholder="Cerca targa..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="controls-right">
                        <div className="results-badge">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="3" width="7" height="7"></rect>
                                <rect x="14" y="3" width="7" height="7"></rect>
                                <rect x="14" y="14" width="7" height="7"></rect>
                                <rect x="3" y="14" width="7" height="7"></rect>
                            </svg>
                            <span>{filteredDati.length} veicoli</span>
                        </div>
                    </div>
                </div>

                {/* Filters Panel */}
                {showFilters && (
                    <div className="filters-panel">
                        <form className="filters-form" onSubmit={handleSubmit}>
                            <div className="filters-grid">
                                <div className="filter-item">
                                    <label>Targa</label>
                                    <input
                                        type="text"
                                        placeholder="Targa..."
                                        value={targa}
                                        onChange={(e) => setTarga(e.target.value.trim())}
                                    />
                                </div>

                                <div className="filter-item">
                                    <label>Tipo Anomalia</label>
                                    <Select
                                        isMulti
                                        options={scenarioOptions}
                                        value={selectedScenarios}
                                        onChange={setSelectedScenarios}
                                        classNamePrefix="rs"
                                        placeholder="Seleziona..."
                                    />
                                </div>

                                <div className="filter-item filter-actions">
                                    <label>&nbsp;</label>
                                    <div className="filter-buttons">
                                        <button type="submit" className="btn-apply-compact">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                            Applica
                                        </button>
                                        <button type="button" className="btn-reset-compact" onClick={handleReset}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                                                <path d="M3 3v5h5"></path>
                                            </svg>
                                            Reset
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                )}

                {/* KPI Stats Cards */}
                <div className="stats-grid-compact">
                    <div className="stat-card-compact stat-success">
                        <div className="stat-icon-compact">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="1" y="3" width="15" height="13"></rect>
                                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                                <circle cx="5.5" cy="18.5" r="2.5"></circle>
                                <circle cx="18.5" cy="18.5" r="2.5"></circle>
                            </svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">Veicoli Attivi</p>
                            <p className="stat-value-compact">{stats.veicoliAttivi}</p>
                        </div>
                    </div>

                    <div className="stat-card-compact stat-danger">
                        <div className="stat-icon-compact">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                <line x1="12" y1="9" x2="12" y2="13"></line>
                                <line x1="12" y1="17" x2="12.01" y2="17"></line>
                            </svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">Veicoli Problematici</p>
                            <p className="stat-value-compact">{stats.veicoliProblematici}</p>
                        </div>
                    </div>

                    <div className="stat-card-compact stat-primary">
                        <div className="stat-icon-compact">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                <polyline points="22 4 12 14.01 9 11.01"></polyline>
                            </svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">Media Conformità</p>
                            <p className="stat-value-compact">{stats.mediaConformita}%</p>
                        </div>
                    </div>

                    <div className="stat-card-compact stat-warning">
                        <div className="stat-icon-compact">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                            </svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">Non Censiti</p>
                            <p className="stat-value-compact">{stats.nonCensiti}</p>
                        </div>
                    </div>
                </div>

                {/* Gantt Chart Modernizzato */}
                <div className="gantt-wrapper">
                    <div className={`gantt-card-modern${isFullscreen ? ' fullscreen-gantt' : ''}`}>
                        <RotateDevice active={isFullscreen} />
                        <div className="fullscreen-bar-inline-gantt">
                            <button
                                className="fullscreen-btn-gantt"
                                onClick={() => setIsFullscreen(!isFullscreen)}
                                title={isFullscreen ? 'Esci da tutto schermo' : 'Tutto schermo'}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    {isFullscreen ? (
                                        <>
                                            <polyline points="4 14 10 14 10 20"></polyline>
                                            <polyline points="20 10 14 10 14 4"></polyline>
                                            <line x1="14" y1="10" x2="21" y2="3"></line>
                                            <line x1="3" y1="21" x2="10" y2="14"></line>
                                        </>
                                    ) : (
                                        <>
                                            <polyline points="15 3 21 3 21 9"></polyline>
                                            <polyline points="9 21 3 21 3 15"></polyline>
                                            <line x1="21" y1="3" x2="14" y2="10"></line>
                                            <line x1="3" y1="21" x2="10" y2="14"></line>
                                        </>
                                    )}
                                </svg>
                            </button>
                        </div>
                        <div className="gantt-header-modern">
                            <div className="gantt-cell vehicle-header-modern">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="1" y="3" width="15" height="13"></rect>
                                    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                                    <circle cx="5.5" cy="18.5" r="2.5"></circle>
                                    <circle cx="18.5" cy="18.5" r="2.5"></circle>
                                </svg>
                                Veicolo
                            </div>
                            {giorni.map((giorno, i) => (
                                <div key={i} className="gantt-cell day-header-modern">
                                    <div className="day-badge">
                                        <span className="day-name-modern">{giorno.dayName}</span>
                                        <span className="day-number-modern">{giorno.dayNumber}</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="gantt-body-modern">
                            {loading ? (
                                <Lf23Loader />
                            ) : filteredDati.length === 0 ? (
                                <div className="empty-state-modern">
                                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <rect x="1" y="3" width="15" height="13"></rect>
                                        <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                                        <circle cx="5.5" cy="18.5" r="2.5"></circle>
                                        <circle cx="18.5" cy="18.5" r="2.5"></circle>
                                        <line x1="1" y1="1" x2="23" y2="23"></line>
                                    </svg>
                                    <h3>Nessun dato disponibile</h3>
                                    <p>Non sono stati trovati veicoli con i filtri selezionati</p>
                                </div>
                            ) : (
                                filteredDati.map((row, idx) => (
                                    <div key={idx} className="gantt-row-modern">
                                        <div className="gantt-cell vehicle-cell-modern">
                                            <div className="vehicle-plate-modern">{row.targa}</div>
                                        </div>
                                        {[...Array(7)].map((_, i) => {
                                            const index = 7 - i;
                                            const cell = row[`giorno_${index}`];
                                            const cellClass = getCellClass(cell);
                                            return (
                                                <div key={i} className="gantt-cell status-cell-modern">
                                                    <div className={`status-box ${cellClass}`}>
                                                        <div className="status-icon-wrapper">
                                                            {getStatusIcon(cell?.stato)}
                                                        </div>
                                                        <div className="status-tooltip-modern">
                                                            <div className="tooltip-header-modern">
                                                                <span className="tooltip-title">
                                                                    Stato {cell?.stato}: {legendMap[cell?.stato]?.label || "N/A"}
                                                                    {cell?.stato === 3 ? (cell?.turno === 0 ? " (senza turno)" : " (con turno)") : ""}
                                                                </span>
                                                                <span className="tooltip-date-modern">{giorni[i].date}</span>
                                                            </div>
                                                            {cell?.dettaglio?.length > 0 && (
                                                                <div className="tooltip-content-modern">
                                                                    <strong>Dettagli:</strong>
                                                                    <ul className="tooltip-list-modern">
                                                                        {cell.dettaglio.map((d, j) => (
                                                                            <li key={j} className="tooltip-item-modern">
                                                                                <span className="detail-type-modern">{d.type}</span>
                                                                                <span className="detail-period-modern">
                                                                                    {d.data_inizio} → {d.data_fine}
                                                                                </span>
                                                                                {d.delta && <span className="detail-delta-modern">({d.delta})</span>}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PercorsiNonMappati;
